import { useEffect, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from '@/app/hooks';
import { useWebinarT } from '@/hooks/useWebinarT';
import { fetchPublicSession } from '@/features/live/liveThunks';
import { setYoutubeClosePending } from '@/features/live/liveSlice';
import type { RootState } from '@/app/store';
import SplashScreen from '@/components/common/SplashScreen';
import AttendeeLivePage from '@/pages/public/AttendeeLivePage';
import ReplayLivePage from '@/pages/public/ReplayLivePage';
import { decideAttendeeLiveRoute } from '@/lib/attendeeLiveRouting';
import { LIVE_ENTRY_TIMEOUT_MS } from '@/lib/liveEntryRouting';
import { useElapsedFlag } from '@/hooks/useElapsedFlag';
import LiveEntryError from '@/components/common/LiveEntryError';

export default function AttendeeLiveGate() {
  const { t } = useWebinarT('webinars');
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { webinarId: webinarIdParam, sessionIdentifier } = useParams<{
    webinarId: string;
    sessionIdentifier: string;
  }>();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');

  const webinarId = webinarIdParam ? parseInt(webinarIdParam, 10) : null;
  const publicSession = useAppSelector((s: RootState) => s.live.publicSession);
  const youtubeClosePending = useAppSelector((s: RootState) => s.live.youtubeClosePending);

  const routeKey = webinarId && sessionIdentifier ? `${webinarId}/${sessionIdentifier}` : null;
  const [confirmedRouteKey, setConfirmedRouteKey] = useState<string | null>(null);
  const [failedRouteKey, setFailedRouteKey] = useState<string | null>(null);

  useEffect(() => {
    if (!webinarId || !sessionIdentifier) return;
    const key = `${webinarId}/${sessionIdentifier}`;
    let cancelled = false;
    // Pas de remise à zéro ici : la confirmation est portée par la clé de route, donc une clé
    // héritée d'un passage antérieur ne correspond déjà plus et ne confirme rien.
    // Repart d'un état propre : seul un live YouTube qui se clôt pendant CE visionnage lèvera le flag.
    dispatch(setYoutubeClosePending(false));
    dispatch(fetchPublicSession({ webinarId, sessionIdentifier }))
      .unwrap()
      .then(() => {
        if (!cancelled) setConfirmedRouteKey(key);
      })
      // Chargement en échec : on ne confirme pas, donc aucune redirection. Repartir sur la valeur
      // en mémoire relancerait l'aller-retour avec la salle d'attente.
      .catch(() => {
        if (!cancelled) setFailedRouteKey(key);
      });
    return () => {
      cancelled = true;
    };
  }, [webinarId, sessionIdentifier, dispatch]);

  const decision = decideAttendeeLiveRoute({
    statusConfirmed: routeKey != null && confirmedRouteKey === routeKey,
    statusUnavailable: routeKey != null && failedRouteKey === routeKey,
    status: publicSession?.session?.status,
    token,
    finishedAt: publicSession?.session?.finishedAt ?? null,
    replayEnabled: publicSession?.session?.replayEnabled ?? false,
    youtubeClosePending,
    nowIso: new Date().toISOString(),
  });

  const redirectTo = decision.kind === 'redirect' ? decision.to : null;

  useEffect(() => {
    if (!redirectTo || !webinarId || !sessionIdentifier || !token) return;
    navigate(`/${redirectTo}/${webinarId}/${sessionIdentifier}?token=${token}`, { replace: true });
  }, [redirectTo, webinarId, sessionIdentifier, token, navigate]);

  // Attente ou redirection : les deux affichent un écran de chargement, les deux doivent finir.
  const entryPending = decision.kind === 'wait' || decision.kind === 'redirect';
  const entryTimedOut = useElapsedFlag(LIVE_ENTRY_TIMEOUT_MS, entryPending);

  // Une route sans identifiants ne se résoudra jamais : état terminal immédiat, pas de faux chargement.
  if (!webinarId || !sessionIdentifier) {
    return <LiveEntryError step="authentication" />;
  }

  switch (decision.kind) {
    case 'wait':
      // Le statut ne se confirme ni ne tombe en erreur : le filet de temps ferme l'attente.
      return entryTimedOut ? (
        <LiveEntryError step="session" />
      ) : (
        <SplashScreen label={t('live.loading')} />
      );
    case 'unavailable':
      return <LiveEntryError step="session" />;
    case 'redirect':
      // La redirection n'aboutit pas : même filet, plutôt qu'un « Redirection… » sans fin.
      return entryTimedOut ? (
        <LiveEntryError step="session" />
      ) : (
        <SplashScreen label={t('live.redirecting')} />
      );
    case 'replay':
      return <ReplayLivePage />;
    case 'finished':
      return <SplashScreen label={t('live.webinarFinished')} />;
    default:
      return <AttendeeLivePage />;
  }
}
